import { useQuery } from '@tanstack/react-query'
import classnames from 'classnames/bind'
import qs from 'qs'
import { useRef } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { getRecommendedProducts } from '~/lib/handle-api'

import Slider from '@unlikelystudio/react-slider'

import Spinner from '~/components/Abstracts/Spinner'
import ProductCard from '~/components/UI/ProductCard'

import { useStyle } from '~/providers/StyleProvider'

import useLang from '~/hooks/useLang'
import useLocale from '~/hooks/useLocale'

import { PRODUCT } from '~/data/dictionary'
import { ProductPageData } from '~/data/product-page-data/serializer'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type RecommendedProductsGridProps = {
  className?: string
  recommendationData?: ProductPageData['recommendationData']
}

function RecommendedProductsGrid({
  className,
  recommendationData,
}: RecommendedProductsGridProps) {
  const customSliderRef = useRef()
  const t = useTranslate()
  const lang = useLang()
  const locale = useLocale()
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.TitleFluid38_144SangBleu,
  })

  const { data: result, isLoading } = useQuery(
    ['products_recommended', recommendationData?.currentStory?.uuid],
    async () => {
      if (recommendationData?.currentStory?.uuid) {
        return getRecommendedProducts({
          payload: {
            query: qs.stringify(
              { ...recommendationData, locale, lang },
              { encode: false },
            ),
          },
        })
      } else {
        return []
      }
    },
  )

  return (
    <div
      className={cx(css.RecommendedProductsGrid, className, {
        hasNoResults: result && result?.items?.length === 0 && !isLoading,
      })}
      ref={customSliderRef}>
      <div className={cx(css.top, gridStyle)}>
        <div className={cx(css.title, titleStyle)}>
          {t(PRODUCT.RECOMMENDED_PRODUCT)}
        </div>
      </div>
      <div className={cx(css.body, gridStyle)}>
        {isLoading && (
          <div className={css.loadingPanel}>
            <Spinner className={css.spinner} />
          </div>
        )}

        {result?.items?.length > 0 && !isLoading && (
          <Slider
            className={cx(css.slider)}
            snap
            customSliderRef={customSliderRef}>
            {result?.items.map((item, index) => (
              <ProductCard
                className={css.item}
                key={`card_recommended_${index}`}
                {...item}
              />
            ))}
          </Slider>
        )}
      </div>
    </div>
  )
}

RecommendedProductsGrid.defaultProps = {}

export default RecommendedProductsGrid
